import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card, Select, DatePicker, Button, Table, Tag, message, Space } from 'antd';
import { SearchOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { ExportExcelButton, PageTitle, StandardCard } from '../common';
import { theme } from '../../theme';
import dayjs, { Dayjs } from 'dayjs';

interface RecepcionDespacho {
  id: number;
  nro_recdes: number;
  fecha: string;
  tipo: 'R' | 'D';
  nro_orden: number;
  tipo_orden: string;
  tipo_orden_meaning: string;
  nro_contrato: number | null;
  destino: string;
  nro_ticket: number;
  chofer: string;
  cantidad_fardos: number;
  kilos_netos: number;
  estado: string;
}

interface Producto {
  id: number;
  descripcion: string;
}

const ListadoRecepcionDespacho: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  
  const [recepcionesDespachos, setRecepcionesDespachos] = useState<RecepcionDespacho[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filtros
  const [idEmpresa, setIdEmpresa] = useState<number>(user?.id_empresa || 1);
  const [idProducto, setIdProducto] = useState<number>(0);
  
  // Predeterminar fechas: últimos 30 días hasta hoy
  const fechaActual = new Date();
  const fechaDesde30Dias = new Date(fechaActual);
  fechaDesde30Dias.setDate(fechaDesde30Dias.getDate() - 30);

  const [desdeFecha, setDesdeFecha] = useState<string>(fechaDesde30Dias.toISOString().split('T')[0]);
  const [hastaFecha, setHastaFecha] = useState<string>(fechaActual.toISOString().split('T')[0]);

  // Cargar productos al montar el componente
  useEffect(() => {
    cargarProductos();
  }, []);

  const cargarProductos = async () => {
    try {
      const token = await getAccessTokenSilently(); 
      // Cargar productos
      const urlProductos = `${process.env.REACT_APP_BALANZA_URL}/productos/`;
      const responseProductos = await fetch(urlProductos, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const dataProductos = await responseProductos.json();
      // Filtrar productos que no sean tipo = 1
      const productosFiltrados = dataProductos.filter((producto: any) => producto.tipo !== 1);
      setProductos(productosFiltrados);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      setError('Error al cargar la lista de productos');
    }
  };

  const buscarRecepcionDespacho = async () => {
    if (!idProducto || !desdeFecha || !hastaFecha) {
      message.warning('Por favor complete todos los filtros requeridos');
      setError('Por favor complete todos los filtros requeridos');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const urlrecdes = `${process.env.REACT_APP_ORDENES_URL}/listado_recepcion_despacho/${idEmpresa}/${idProducto}/${desdeFecha}/${hastaFecha}/`;
 
      const response = await api.get(
        urlrecdes,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setRecepcionesDespachos(response.data);
    } catch (error: any) {
      console.error('Error al buscar recepciones/despachos:', error);
      setError(error.response?.data?.error || 'Error al buscar recepciones/despachos');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    // Implementar exportación a Excel
    console.log('Exportar a Excel');
  };

  const irANuevo = () => {
    navigate('/recepcion_despacho');
  };


  const getTipoBadge = (tipo: string) => {
    return tipo === 'R' ? (
      <Tag color="success">Recepción</Tag>
    ) : (
      <Tag color="blue">Despacho</Tag>
    );
  };

  const getEstadoBadge = (estado: string) => {
    return estado === 'Activo' ? (
      <Tag color="success">Activo</Tag>
    ) : (
      <Tag color="error">Inactivo</Tag>
    );
  };

  const formatFecha = (fecha: string) => {
    try {
      const date = new Date(fecha);
      return date.toLocaleDateString('es-ES');
    } catch {
      return fecha;
    }
  };

  // Columnas para la tabla
  const columns = [
    {
      title: 'Nro. Rec/Des',
      dataIndex: 'nro_recdes',
      key: 'nro_recdes',
      width: 120,
      render: (text: number) => <strong>{text}</strong>,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (fecha: string) => formatFecha(fecha),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      render: (tipo: string) => getTipoBadge(tipo),
    },
    {
      title: 'Nro. Orden',
      dataIndex: 'nro_orden',
      key: 'nro_orden',
      width: 120,
    },
    {
      title: 'Tipo Orden',
      dataIndex: 'tipo_orden_meaning',
      key: 'tipo_orden_meaning',
      width: 150,
      render: (text: string, record: RecepcionDespacho) => (
        <Tag color="cyan" title={`${record.tipo_orden} - ${text}`}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Nro. Contrato',
      dataIndex: 'nro_contrato',
      key: 'nro_contrato',
      width: 120,
      render: (text: number | null) => text || '-',
    },
    {
      title: 'Destino',
      dataIndex: 'destino',
      key: 'destino',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Nro. Ticket',
      dataIndex: 'nro_ticket',
      key: 'nro_ticket',
      width: 120,
    },
    {
      title: 'Chofer',
      dataIndex: 'chofer',
      key: 'chofer',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Unidades',
      dataIndex: 'cantidad_fardos',
      key: 'cantidad_fardos',
      width: 100,
      align: 'center' as const,
      render: (text: number) => <strong>{text}</strong>,
    },
    {
      title: 'Peso',
      dataIndex: 'kilos_netos',
      key: 'kilos_netos',
      width: 120,
      align: 'right' as const,
      render: (text: number) => <strong>{text?.toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}</strong>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      render: (estado: string) => getEstadoBadge(estado),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: RecepcionDespacho) => (
        <Button
          type="primary"
          icon={<FileTextOutlined />}
          onClick={() => {
            navigate(`/emisionrecdes/${record.id}`);
          }}
          size="middle"
          style={{
            height: '32px',
            fontSize: '13px',
            fontWeight: 500,
            minWidth: '80px'
          }}
        >
          Emisión
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Título y botón Nuevo */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <PageTitle level={3} style={{ fontSize: '22px', marginBottom: 0, fontWeight: 600 }}>
          Recepción/Despacho
        </PageTitle>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={irANuevo}
          size="middle"
          style={{
            height: '32px',
            fontSize: '13px',
            fontWeight: 500,
            minWidth: '100px'
          }}
        >
          Nuevo
        </Button>
      </div>

      <StandardCard>
        {/* Filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
          alignItems: 'end'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 500,
              color: theme.colors.textPrimary,
              fontSize: '13px'
            }}>
              Producto <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <Select
              value={idProducto || undefined}
              onChange={(value) => setIdProducto(value)}
              placeholder="Seleccionar producto"
              size="middle"
              style={{ width: '100%' }}
            >
              {productos.map((producto) => (
                <Select.Option key={producto.id} value={producto.id}>
                  {producto.descripcion}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 500,
              color: theme.colors.textPrimary,
              fontSize: '13px'
            }}>
              Desde Fecha <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <DatePicker
              value={desdeFecha ? dayjs(desdeFecha) : null}
              onChange={(date: Dayjs | null) => setDesdeFecha(date ? date.format('YYYY-MM-DD') : '')}
              size="middle"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 500,
              color: theme.colors.textPrimary,
              fontSize: '13px'
            }}>
              Hasta Fecha <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <DatePicker
              value={hastaFecha ? dayjs(hastaFecha) : null}
              onChange={(date: Dayjs | null) => setHastaFecha(date ? date.format('YYYY-MM-DD') : '')}
              size="middle"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </div>

          <div>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={buscarRecepcionDespacho}
                loading={loading}
                size="middle"
                style={{
                  height: '32px',
                  fontSize: '13px',
                  fontWeight: 500
                }}
              >
                Buscar
              </Button>
              <ExportExcelButton
                onClick={exportarExcel}
                disabled={recepcionesDespachos.length === 0}
                size="middle"
              />
            </Space>
          </div>
        </div>

        {/* Mensaje de error */}
        {error && (
          <div style={{
            padding: '8px 12px',
            marginBottom: '16px',
            backgroundColor: '#fff2f0',
            border: `1px solid ${theme.colors.error}`,
            borderRadius: '4px',
            color: theme.colors.error,
            fontSize: '13px'
          }}>
            {error}
          </div>
        )}

        {/* Tabla de resultados */}
        {recepcionesDespachos.length > 0 && (
          <>
            <StandardCard style={{ marginBottom: '16px' }}>
              <Table
                columns={columns}
                dataSource={recepcionesDespachos}
                rowKey="id"
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                  pageSizeOptions: ['10', '25', '50', '100'],
                  position: ['bottomRight']
                }}
                scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 500px))' }}
                size="middle"
              />
            </StandardCard>
            
            {/* Totales */}
            <StandardCard style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                textAlign: 'center'
              }}>
                <div>
                  <div style={{ 
                    fontWeight: 600, 
                    color: theme.colors.primary,
                    fontSize: '13px',
                    marginBottom: '6px'
                  }}>
                    Cantidad Registros
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 700,
                    color: theme.colors.textPrimary
                  }}>
                    {recepcionesDespachos.length}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontWeight: 600, 
                    color: theme.colors.success,
                    fontSize: '13px',
                    marginBottom: '6px'
                  }}>
                    Total Unidades
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 700,
                    color: theme.colors.textPrimary
                  }}>
                    {recepcionesDespachos.reduce((sum, item) => sum + (item.cantidad_fardos || 0), 0).toLocaleString('es-ES')}
                  </div>
                </div>
                <div>
                  <div style={{ 
                    fontWeight: 600, 
                    color: theme.colors.info,
                    fontSize: '13px',
                    marginBottom: '6px'
                  }}>
                    Total Peso
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 700,
                    color: theme.colors.textPrimary
                  }}>
                    {recepcionesDespachos.reduce((sum, item) => sum + (item.kilos_netos || 0), 0).toLocaleString('es-ES', {minimumFractionDigits: 2, maximumFractionDigits: 2})} kg
                  </div>
                </div>
              </div>
            </StandardCard>
          </>
        )}

        {/* Mensaje cuando no hay resultados */}
        {!loading && recepcionesDespachos.length === 0 && !error && (
          <StandardCard>
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.colors.textSecondary
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
              <p style={{ fontSize: '14px', marginBottom: '8px' }}>No se encontraron registros de recepción/despacho</p>
              <p style={{ fontSize: '13px', color: theme.colors.textMuted }}>Utilice los filtros para buscar registros</p>
            </div>
          </StandardCard>
        )}
      </StandardCard>
    </div>
  );
};

export default ListadoRecepcionDespacho; 